import { X } from 'lucide-react';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { StepKind } from '../types';

/**
 * Other div props are passed through, so the card can be Radix popover content (`asChild`):
 * its side and alignment attributes, transform-origin variables and dismiss handlers land on it.
 */
export interface StepCardProps extends Omit<ComponentPropsWithoutRef<'div'>, 'title' | 'children'> {
  kind: StepKind;
  title: string;
  body: string;
  /** 0-based position among the counted steps (the finish card is not counted). */
  index: number;
  total: number;
  /** Shown under the body when the target is not on screen. */
  missingNote?: string | undefined;
  /** Finish card: buttons for related tours. */
  related?: ReadonlyArray<{ id: string; title: string }>;
  onRelated?: (id: string) => void;
  canGoBack: boolean;
  onNext: () => void;
  onBack: () => void;
  onEnd: () => void;
  reducedMotion: boolean;
  /** Full-width sheet below the md breakpoint. */
  sheet?: boolean;
  /** The screen edge the sheet sits on: the top when the target is in the lower half. */
  sheetEdge?: 'top' | 'bottom';
  /** Anchored cards: the arrow pointing at the target, rendered last inside the card. */
  arrow?: ReactNode;
}

/**
 * The tour's step card: counter, title, body, progress dots and controls. Explain, intro and
 * finish cards are modal (focus stays inside); try-it cards are not, so the target stays
 * reachable. Keyboard handling lives in the tour layer, which also knows the target.
 */
export const StepCard = forwardRef<HTMLDivElement, StepCardProps>(function StepCard(
  {
    kind,
    title,
    body,
    index,
    total,
    missingNote,
    related = [],
    onRelated,
    canGoBack,
    onNext,
    onBack,
    onEnd,
    reducedMotion,
    sheet = false,
    sheetEdge = 'bottom',
    arrow,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const bodyId = useId();
  const isAction = kind === 'action' && !missingNote;
  const isFinish = kind === 'finish';

  const motion = reducedMotion
    ? 'animate-in fade-in-0 duration-100'
    : cn(
        'animate-in fade-in-0 duration-150 motion-reduce:duration-100',
        !sheet
          ? 'zoom-in-95 motion-reduce:zoom-in-100'
          : sheetEdge === 'top'
            ? 'slide-in-from-top-4 motion-reduce:slide-in-from-top-0'
            : 'slide-in-from-bottom-4 motion-reduce:slide-in-from-bottom-0',
      );

  return (
    <div
      {...rest}
      ref={ref}
      role="dialog"
      aria-modal={isAction ? undefined : true}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-tour-card={kind}
      className={cn(
        'bg-card text-card-foreground pointer-events-auto flex flex-col gap-3 rounded-xl border p-4 shadow-lg',
        sheet ? 'w-full' : 'w-80 max-w-[calc(100vw-2rem)]',
        motion,
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs font-medium tabular-nums">
          {isFinish ? t('tour.finished') : t('tour.stepOf', { current: index + 1, total })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-mt-1 -mr-2 size-7"
          aria-label={t('tour.end')}
          onClick={onEnd}
        >
          <X aria-hidden />
        </Button>
      </div>
      <div className="flex flex-col gap-1.5">
        <h2
          id={titleId}
          tabIndex={-1}
          className="text-base leading-snug font-semibold outline-none"
        >
          {title}
        </h2>
        <p id={bodyId} className="text-muted-foreground text-sm leading-relaxed">
          {body}
          {missingNote ? (
            <span className="text-foreground mt-1.5 block text-xs">{missingNote}</span>
          ) : null}
        </p>
      </div>
      {isFinish && related.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium">{t('tour.whatNext')}</span>
          <ul className="flex flex-col gap-1">
            {related.map((item) => (
              <li key={item.id}>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => onRelated?.(item.id)}
                >
                  {t('tour.relatedTour', { title: item.title })}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3 pt-1">
        {isFinish ? (
          <span />
        ) : (
          <ProgressDots index={index} total={total} reducedMotion={reducedMotion} />
        )}
        <div className="flex items-center gap-2">
          {canGoBack && !isFinish ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              aria-label={t('tour.backLabel')}
            >
              {t('tour.back')}
            </Button>
          ) : null}
          {isAction ? (
            <>
              <span className="text-primary text-xs font-medium">{t('tour.yourTurn')}</span>
              <Button type="button" variant="outline" size="sm" onClick={onNext}>
                {t('tour.skipStep')}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={onNext}
              aria-label={isFinish ? undefined : t('tour.nextLabel')}
            >
              {isFinish ? t('tour.done') : t('tour.next')}
            </Button>
          )}
        </div>
      </div>
      {arrow}
    </div>
  );
});

function ProgressDots({
  index,
  total,
  reducedMotion,
}: {
  index: number;
  total: number;
  reducedMotion: boolean;
}): ReactNode {
  return (
    <span className="flex items-center gap-1" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          data-current={i === index || undefined}
          className={cn(
            'h-1.5 rounded-full',
            !reducedMotion &&
              'transition-[width,background-color] duration-200 motion-reduce:transition-none',
            i === index
              ? 'bg-primary w-[18px]'
              : i < index
                ? 'bg-primary/60 w-1.5'
                : 'bg-muted w-1.5',
          )}
        />
      ))}
    </span>
  );
}
